import React, { useContext } from 'react'
import TextField from '@material-ui/core/TextField'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { FieldRenderProps } from 'react-final-form'
import classNames from 'classnames'
import { ClassValue } from 'classnames/types'
import styles from '../../styles'
import { FormattedMessageModel } from './types/fieldsModel'
import { WHITE } from '../../theme'
import { ResponsiveContext } from '../../utils/context/responsiveContext'
import { useIntl } from 'react-intl'
import { Grid } from '@material-ui/core'

const useStyles = makeStyles<Theme, { isLargeScreen: boolean }>(
	(theme) => (
		{
			...styles(theme),
			label: {
				...styles(theme).text5,
				color: theme.palette.blue.main,
				marginBottom: 15
			},
			textFieldStyle: {
				width: '100%',
				boxSizing: 'border-box',
				marginTop: 10,
				backgroundColor: WHITE,
				padding: '0px 20px',

				'& .MuiInputLabel-shrink': {
					color: theme.palette.green.main,
					marginTop: 8
				},

				'& .MuiInputLabel-formControl:not(.MuiInputLabel-shrink)': {
					transform: 'translate(0, 18px) scale(1)'
				},

				'& input': {
					minHeight: 30,
					...styles(theme).text1,
					fontSize: 15,
					fontWeight: 500,
					lineHeight: 1.33
				},
				'& textarea': {
					...styles(theme).text1,
					fontSize: props => props.isLargeScreen ? 18 : 16,
					fontWeight: 500,
					opacity: 0.7,
					lineHeight: 1.33
				}
			},
			errorStyle: {
				color: '#B92D00',
				fontSize: 10
			},
			errorColor: {
				color: '#B92D00 !important'
			},
			errorBorder: {
				border: '1px solid #B92D00'
			},
			disabledStyle: {
				'& input': {
					opacity: '0.7 !important'
				},
				'& .MuiInput-underline:before': {
					borderBottom: 0
				}
			},
			textFontSize: {
				fontSize: 16
			},
			defaultTextFieldContainerStyle: {
				width: '100%'
			}
		}
	)
)

interface AcceptingProps {
	id: string,
	label?: string,
	placeholder?: FormattedMessageModel,
	maxLength?: number,
	disabled?: boolean,
	multiline?: boolean,
	textFieldContainerStyle?: string,
	suffixComponent?: JSX.Element
}

type FormTextFieldInlineProps =
	FieldRenderProps<string>
	& AcceptingProps

const FormTextFieldInline: React.FC<FormTextFieldInlineProps> = (
	{
		input,
		meta: {
			touched,
			error
		},
		id,
		label,
		placeholder,
		maxLength,
		disabled,
		multiline,
		textFieldContainerStyle,
		suffixComponent
	}
) => {
	const { isLargeScreen } = useContext(ResponsiveContext)
	const classes = useStyles({ isLargeScreen })
	const intl = useIntl()
	const textFieldClass: ClassValue = classNames(
		classes.textFieldStyle,
		{
			[classes.errorBorder]: touched && error,
			[classes.disabledStyle]: disabled
		}
	)

	const checkKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === '"') {
			e.preventDefault()
		}
	}

	return (
		<div
			className={
				classNames(
					classes.defaultTextFieldContainerStyle,
					textFieldContainerStyle
				)
			}
		>
			<Grid item xs={12}>
				<span
					className={classNames(
						classes.label,
						{
							[classes.errorStyle]: touched && error
						}
					)}
				>
					{label}
				</span>
			</Grid>

			<Grid container alignItems="center" item xs={12}>
				<Grid item xs={suffixComponent ? 6 : 12} md={suffixComponent ? 'auto' : 12}>
					<TextField
						id={id}
						onKeyDown={checkKeyDown}
						disabled={disabled}
						placeholder={placeholder ? intl.formatMessage({
							id: placeholder.id,
							defaultMessage: placeholder.defaultMessage,
							description: placeholder.description
						}, placeholder.values) : undefined}
						InputProps={{ disableUnderline: true }}
						className={textFieldClass}
						multiline={multiline}
						inputProps={{ maxLength: maxLength }}
						{...input}
					/>
				</Grid>

				{
					suffixComponent && (
						<Grid item>
							{suffixComponent}
						</Grid>
					)
				}
			</Grid>

			{
				touched &&
				((error && <span className={classes.errorStyle}>{error}</span>))
			}
		</div>
	)
}

export default FormTextFieldInline
